import { FC, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { ResponsiveBlock } from 'client/components/ResponsiveBlock/ResponsiveBlock'

import { motion, AnimatePresence, wrap, Transition, Variants } from 'motion/react'
import { Button } from 'client/components/Button/Button'
import useResizeObserver from 'use-resize-observer'
import { ProductsPage } from './components/ProductPage/ProductPage'
import { chunk } from 'lib/utils/chunk'
import { useTranslation } from 'react-i18next'
import { ClientLocaleNS } from 'lib/constants/locales'

type TProps = {}

type TPageProps = {
  products: Data.Product[]
}

const PRODUCTS_PER_PAGE = 8
const sliderVariants: Variants = {
  incoming: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  active: { x: 0, opacity: 1 },
  exit: (direction) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0.2,
  }),
}

const sliderTransition: Transition = {
  bounce: false,
  duration: 0.5,
  ease: 'linear',
}

const ProductsBlock: FC<TProps> = () => {
  const {
    props: { products },
  } = usePage<TPageProps>()
  const { t } = useTranslation(ClientLocaleNS.HomePage)
  const ref = useRef<HTMLDivElement>(null)
  //@ts-expect-error
  const { width, height } = useResizeObserver({ ref })
  const chunks = chunk(products, PRODUCTS_PER_PAGE)

  const [[pageCount, direction], setPageCount] = useState([0, 0])

  const swipeToImage = (swipeDirection: number) => {
    if (swipeDirection > 0) {
      if (pageCount + 1 < chunks.length) {
        setPageCount([pageCount + 1, swipeDirection])
      }
    } else {
      if (pageCount >= 1) {
        setPageCount([pageCount - 1, swipeDirection])
      }
    }
  }

  const productsChunk = chunks[pageCount]

  return (
    <ResponsiveBlock className="mb-30" contentClassName="flex-col w-full">
      <div ref={ref} className="flex w-full overflow-hidden h-full">
        <AnimatePresence custom={direction} initial={false} mode="popLayout">
          <motion.div
            className="flex w-full h-full"
            key={pageCount}
            custom={direction}
            variants={sliderVariants}
            initial="incoming"
            animate="active"
            exit="exit"
            transition={sliderTransition}
            style={{ minHeight: height }}
          >
            <ProductsPage products={productsChunk} style={{ width }}></ProductsPage>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex w-full justify-center pt-12">
        <div className="flex items-center gap-8">
          <Button onClick={() => swipeToImage(-1)}>{t('products_block.prev')}</Button>
          <Button onClick={() => swipeToImage(1)}>{t('products_block.next')}</Button>
        </div>
      </div>
    </ResponsiveBlock>
  )
}

export { ProductsBlock }
